/**
 * NextSlotWidget — small "Next available slot" CTA pill.
 *
 * Renders as a compact strip that reads e.g. "Next slot · Tomorrow 10:30 am
 * with Krina · Book →" and, on click, opens the booking modal deep-linked
 * to the correct category. Silently hides if the backend returns
 * available=false or the fetch fails — this is a marketing sweetener,
 * never a blocker.
 *
 * Uses the `/api/booking/next-slot` public endpoint (10-min server-side
 * cache), so multiple copies on the same page (hero + service page) don't
 * multiply outbound WriteUpp calls.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarClock, Loader2, ArrowRight } from 'lucide-react';
import { useBookingModal } from '../context/BookingContext';
import { trackEvent } from '../lib/track';
import { NEXT_SLOT_FEATURE } from '../config/featureFlags';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * @param {object} props
 * @param {string} [props.category]  — matches CATEGORY_META in BookingContext (tmj, botox, massage, velvet, nesa, virtual, followup)
 * @param {string} [props.variant]   — 'hero' (large) or 'inline' (compact). Default 'inline'.
 * @param {string} [props.source]    — analytics attribution (page/section)
 */
const NextSlotWidget = ({ category, variant = 'inline', source }) => {
  const { openBooking } = useBookingModal();
  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!NEXT_SLOT_FEATURE) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    axios
      .get(`${API}/booking/next-slot`, { params: category ? { category } : {} })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.available) {
          setSlot(res.data);
        } else {
          setSlot(null);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [category]);

  // Feature parked (see /app/frontend/src/config/featureFlags.js). Flip
  // NEXT_SLOT_FEATURE back to `true` to re-enact — everything else stays wired.
  if (!NEXT_SLOT_FEATURE) return null;

  const handleClick = () => {
    trackEvent('next_slot_widget_click',
      `category=${category || 'any'} · when=${slot?.when_human || 'unknown'}${source ? ` · from=${source}` : ''}`
    );
    openBooking(category ? { category, source: source || 'next-slot-widget' } : { source: source || 'next-slot-widget' });
  };

  if (failed) return null;

  if (loading) {
    return (
      <div className={`next-slot next-slot-${variant} next-slot-loading`} data-testid="next-slot-loading" aria-hidden>
        <Loader2 size={variant === 'hero' ? 14 : 12} className="animate-spin" />
        <span>Checking next available slot…</span>
      </div>
    );
  }

  if (!slot) return null;

  const firstName = (slot.practitioner || '').split(' ')[0];

  return (
    <button
      type="button"
      className={`next-slot next-slot-${variant}`}
      onClick={handleClick}
      data-testid={`next-slot-cta${category ? `-${category}` : ''}`}
      aria-label={`Book next available slot: ${slot.when_human}${firstName ? ` with ${firstName}` : ''}`}
      title={slot.cached ? 'Live diary · refreshed every 10 min' : 'Live diary'}
    >
      <span className="next-slot-icon" aria-hidden><CalendarClock size={variant === 'hero' ? 16 : 13} /></span>
      <span className="next-slot-copy">
        <span className="next-slot-lead">
          <span className="next-slot-pulse" aria-hidden />
          Next slot
        </span>
        <span className="next-slot-when">
          {slot.when_human}
          {firstName ? <span className="next-slot-who"> · with {firstName}</span> : null}
          {variant === 'hero' && (slot.duration_min || slot.price_gbp) ? (
            <span className="next-slot-fit">
              {slot.duration_min ? ` · ${slot.duration_min} min` : ''}
              {slot.price_gbp ? ` · ${category ? '' : 'from '}£${Math.round(slot.price_gbp)}` : ''}
            </span>
          ) : null}
          {slot.slots_left && slot.slots_left > 0 ? (
            <span
              className={`next-slot-scarcity ${slot.slots_left <= 3 ? 'is-tight' : ''}`}
              data-testid="next-slot-scarcity"
            >
              {' · '}Only <strong>{slot.slots_left}</strong> slot{slot.slots_left === 1 ? '' : 's'} left {slot.slots_left_label || 'this week'}
            </span>
          ) : null}
        </span>
      </span>
      <span className="next-slot-cta" aria-hidden>
        {variant === 'hero' ? 'Book instantly' : 'Book'} <ArrowRight size={variant === 'hero' ? 14 : 12} />
      </span>
    </button>
  );
};

export default NextSlotWidget;
